'use client';

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { ConfirmRequest } from '@modules/garden-planner/models';
import type { Garden, Plant } from '@modules/garden-planner/business_logic';

export interface GardenPlannerContextValue {
  garden: Garden;
  plants: Plant[];
  onConfirm: (request: ConfirmRequest) => void;
  onShareCopy: () => Promise<void>;
}

const GardenPlannerContext = createContext<GardenPlannerContextValue | null>(null);

interface GardenPlannerProviderProps {
  children: ReactNode;
  garden: Garden;
  plants: Plant[];
  onConfirm: (request: ConfirmRequest) => void;
  onShareCopy: () => Promise<void>;
}

export function GardenPlannerProvider({
  children,
  garden,
  plants,
  onConfirm,
  onShareCopy,
}: GardenPlannerProviderProps) {
  const value = useMemo(
    () => ({ garden, plants, onConfirm, onShareCopy }),
    [garden, plants, onConfirm, onShareCopy]
  );

  return <GardenPlannerContext.Provider value={value}>{children}</GardenPlannerContext.Provider>;
}

export function useGardenPlannerContext() {
  const context = useContext(GardenPlannerContext);
  if (!context) {
    throw new Error('useGardenPlannerContext must be used within GardenPlannerProvider');
  }
  return context;
}

/** Garden document from the active planner query — non-null below the loading guard. */
export function useGarden() {
  return useGardenPlannerContext().garden;
}
